import React, {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {cleanUpObject, createAnalyticsItemsList, findCategoryById, getMediaPath} from '../../../../app/functions';
import {AnalyticsParamValues} from '../../../../constants/analytics';
import * as routes from '../../../../constants/routes';
import {
    HOMEPAGE_NAV_ITEM_TAB,
    HOMEPAGE_NAV_ITEM_BUTTON,
    HOMEPAGE_DEFAULT_SLIDER_TAB,
} from 'constants/homepage-types';
import SliderHeader from '../slider-header';
import SliderContent from '../slider-content';
import PromotionSlide from '../promotion-slide';
import Benefit from '../../../shared/benefit/benefit';
import expertIconPath from 'assets/icons/category-slide/action-buttons/ask-expert.svg';
import magazineIconPath from 'assets/icons/category-slide/action-buttons/tips.svg';
import './index.scss';
import Analytics from "app/analytics";

const CategorySlider = ({data, creativeSlot, promotionName}) => {
    const history = useHistory();

    const categories = useSelector((store) => cleanUpObject(store.config.categoriesArr).Data);
    const translations = useSelector((store) => store.config.Translations);

    const [selectedTab, setSelectedTab] = useState(HOMEPAGE_DEFAULT_SLIDER_TAB);
    const [headerTabs, setHeaderTabs] = useState([]);
    const [headerButtons, setHeaderButtons] = useState([]);
    const [contentItems, setContentItems] = useState([]);

    const sliderId = data['id'];
    const sliderTitle = data['title'];
    const sliderIconPath = getMediaPath(data['icon_img']);
    const sliderShowMoreButton = data['btnText'];
    const sliderAnalyticsType = AnalyticsParamValues.CATEGORY_BENEFITS_SLIDER;
    const sliderAnalyticsSource = AnalyticsParamValues.HOMEPAGE_SLIDER_SOURCE;

    const displayExpertButton = Number(data['expert_opinion_btn']);
    const displayMagazineButton = Number(data['smart_consuming_btn']);

    const mainCategoryData = findCategoryById(categories, data?.['main_category']?.['id']) || null;
    const subCategoryData = findCategoryById(categories, data?.['sub_categories']?.[selectedTab]?.['id']) || null;

    const subCategoryId = subCategoryData?.['id'] || null;
    const subCategoryTitle = subCategoryData?.['name'] || subCategoryData?.['title'] || null;

    const subCategoriesList = data['sub_categories'];

    const mainCategoryLink = (() => {
        if (mainCategoryData) {
            return `${routes.category}/${mainCategoryData.id}/${mainCategoryData.url_name}`;
        } else {
            return routes.benefits;
        }
    })();

    const subCategoryLink = (() => {
        if (subCategoryData) {
            return `${routes.category}/${subCategoryData.id}/${subCategoryData.url_name}`;
        }

        return mainCategoryLink;
    })();

    const redirectToCategory = () => {
        history.push(mainCategoryLink)
    };

    const getHeaderTabs = () => {
        if (subCategoriesList.length > 0) {
            return subCategoriesList.map((subCategoryData, subCategoryIndex) => ({
                key: `sub-category-${subCategoryIndex}`,
                index: subCategoryIndex,
                title: subCategoryData['title'],
                type: HOMEPAGE_NAV_ITEM_TAB,
            }));
        }

        return [];
    };

    const getCategory = () => {
        if(selectedTab === HOMEPAGE_DEFAULT_SLIDER_TAB){
            return data?.['main_category'] || null;
        } else {
            return data?.['sub_categories']?.[selectedTab] || null;
        }
    }

    const getBenefits = () => {
        let category = getCategory();
        let benefits = category?.['related_benefits'] || [];
        if (!benefits?.length) {
            benefits = data['sub_categories']
            .flatMap(subCategory => subCategory['related_benefits'])
            .sort( (a, b) => Number(a['order_num']) - Number(b['order_num']) );    
        } 

        return (benefits||[])
    }

    const logViewPromotion = (selectedTab) => {
        const category = getCategory();
        const tabPromotionName = category?.['title'] || promotionName;
        const itemsForLog = createAnalyticsItemsList(getBenefits(), {item_list_name: tabPromotionName})
        Analytics.logViewMainSliderTabHomePage(itemsForLog, creativeSlot, tabPromotionName)
    }

    const logSelectPromotion = (item, promotionName, creativeSlot, index) => {
        const itemsForLog = createAnalyticsItemsList([item], {item_list_name: promotionName, index: index})
        Analytics.logSelectPromotion(itemsForLog, creativeSlot, promotionName)
    }

    useEffect(() => {
        logViewPromotion(HOMEPAGE_DEFAULT_SLIDER_TAB)
    }, [])

    const getHeaderButtons = () => {
        const buttonsList = [];

        if (displayExpertButton) {
            const categoryExpert = Number(mainCategoryData?.['relatedExpert']);

            buttonsList.push({
                key: 'expert-button',
                text: translations['category_expert_button'],
                href: categoryExpert ? `${routes.experts}?id=${categoryExpert}` : routes.experts,
                iconPath: expertIconPath,
                iconModifier: 'expert-icon',
                iconDir: 'right',
                type: HOMEPAGE_NAV_ITEM_BUTTON,
            });
        }

        if (displayMagazineButton) {
            const categoryMagazine = mainCategoryData?.['relatedMagazine'];

            buttonsList.push({
                key: 'magazine-button',
                text: categoryMagazine
                    ? `${translations['category_magazine_button']} ${categoryMagazine}`
                    : translations['category_magazine_button'],
                href: categoryMagazine
                    ? `${routes.magazine}/${categoryMagazine}`
                    : routes.magazine,
                iconPath: magazineIconPath,
                iconModifier: 'magazine-icon',
                iconDir: 'right',
                type: HOMEPAGE_NAV_ITEM_BUTTON,
            });
        }

        return buttonsList;
    };


    const getContentItems = () => {
        let category = getCategory();
        const categoryBenefits = getBenefits();
        const promotionName = category?.['title'] || sliderTitle;
        const onClick = (item, index) => {
            logSelectPromotion(item, promotionName, creativeSlot, index)
        }
        return categoryBenefits.map((benefitData, index) => (
            <Benefit data={benefitData}
                     analyticsSource={sliderAnalyticsSource}
                     onClick={(item) => onClick(item, index)}
            />
        ));
    };

    useEffect(() => {
        setHeaderTabs(getHeaderTabs);
        setHeaderButtons(getHeaderButtons);
        setContentItems(getContentItems);
    }, [data]);


    useEffect(() => {
        setContentItems(getContentItems);

        if (selectedTab !== HOMEPAGE_DEFAULT_SLIDER_TAB) {
            logViewPromotion(selectedTab)
        }
    }, [selectedTab]);

    return (
        <div className="homepage-category-slider">
            <SliderHeader
                id={sliderId}
                title={sliderTitle}
                iconPath={sliderIconPath}
                onTitleClick={redirectToCategory}
                showMoreButton={sliderShowMoreButton}
                onShowMoreButtonClick={redirectToCategory}
                tabsList={headerTabs}
                buttonsList={headerButtons}
                selectedTab={selectedTab}
                onTabSelect={setSelectedTab}
                analyticsType={sliderAnalyticsType}
            />

            <SliderContent
                itemsList={contentItems}
                creativeSlot={creativeSlot}
                scrollByVisibleSlides
                promotionSlide={(
                    <PromotionSlide
                        link={subCategoryLink}
                        sliderId={sliderId}
                        sliderTitle={sliderTitle}
                        sliderAnalyticsType={sliderAnalyticsType}
                        tabId={subCategoryId}
                        tabTitle={subCategoryTitle}
                    />
                )}
                selectedTab={selectedTab}
            />
        </div>
    );
};

export default CategorySlider;
